import { useState } from "react";
import { ThumbsUp, ThumbsDown, Edit3, Trash2, User, Bot, CheckCircle, XCircle, ChevronDown } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

export const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="border-slate-200">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const FeedbackCard = ({ feedback, onEdit, onDelete, onToggleActive }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className={`border ${feedback.is_active ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-60"}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Rating badge */}
            <div className="flex items-center gap-2 mb-2">
              {feedback.rating === "positive" ? (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  <ThumbsUp className="w-3 h-3 mr-1" />
                  Good Response
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 border-red-200">
                  <ThumbsDown className="w-3 h-3 mr-1" />
                  Needs Correction
                </Badge>
              )}
              {feedback.category && (
                <Badge variant="outline" className="text-xs">
                  {feedback.category}
                </Badge>
              )}
              {!feedback.is_active && (
                <Badge variant="outline" className="text-xs text-slate-400">
                  Disabled
                </Badge>
              )}
            </div>
            
            {/* Question */}
            <div className="flex items-start gap-2 mb-2">
              <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 line-clamp-2">{feedback.original_question}</p>
            </div>
            
            {/* Show more on expand */}
            {expanded && (
              <div className="space-y-3 mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-start gap-2">
                  <Bot className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-red-500 mb-1">Original AI Response</p>
                    <p className="text-sm text-slate-600">{feedback.original_ai_response}</p>
                  </div>
                </div>
                
                {feedback.corrected_response && (
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-emerald-600 mb-1">Correct Response</p>
                      <p className="text-sm text-slate-700">{feedback.corrected_response}</p>
                    </div>
                  </div>
                )}
                
                {feedback.notes && (
                  <div className="text-xs text-slate-500 italic">
                    Notes: {feedback.notes}
                  </div>
                )}
                
                <div className="text-xs text-slate-400 flex items-center gap-3">
                  <span>Added: {new Date(feedback.created_at).toLocaleDateString()}</span>
                  {feedback.created_by && <span>By: {feedback.created_by}</span>}
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setExpanded(!expanded)}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Edit3 className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(feedback)}>
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleActive(feedback)}>
                  {feedback.is_active ? (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Disable
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Enable
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(feedback)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
